/** @shell standard @header bar 
 * Flow 2 only: guest picked a plan — preview menu layout; interactions prompt signup → Register → checkout.
 *
 * Menu data: `fetchGuestMenuPreview` → `GET` `endpoints.menus` with `customer_id: 0` (org preview menu for the date).
 * Query key is `['guest-menu-preview', date]` only (not plan-specific) so all guests share one cached menu for that day.
 * If the API fails or returns no builders, we fall back to `buildMockGuestMenuBuilders()`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Alert, Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { showActionSheet } from '../components/ui/showActionSheet';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import type { RootStackParamList } from '../navigation/types';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';
import { DASH_STUDIO_INNER } from '../shared/theme/studio/dashboardConstants';
import { MenuFlowItemCard } from '../menu/components/MenuFlowItemCard';
import { fetchGuestMenuPreview, type MenuBuilder, type MenuItemData } from '../data/domains/menu';
import { resolveQueryRetry } from '../data/query/queryRetry';
import { getGuestExplorePlan, clearGuestExplorePlan } from '../data/domains/guest';
import { setSelectedSubscription } from '../data/domains/subscription';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { useAuthStore } from '../shared/auth/store';
import { appTheme, useThemeColors, useThemeStore, type ThemeColors } from '../shared/theme';
import { useTranslation, getApiText } from '../shared/i18n';

function buildMockGuestMenuBuilders(): MenuBuilder[] {
  const base_item = (over: Partial<MenuItemData> & Pick<MenuItemData, 'id' | 'name' | 'food_type_category'>): MenuItemData =>
    ({
      quantity: 0,
      is_selected: false,
      food_type_id: 1,
      is_customizations_available: 0,
      scaled_carbs: 50,
      protein: '18',
      calories: '420',
      fats: '12',
      carbs: '45',
      customizations: [],
      menu_builder_id: 10001,
      selection_number: 0,
      slug: 'guest-preview',
      description: undefined,
      a_description: undefined,
      attachment: undefined,
      ...over,
      id: over.id,
      name: over.name,
      a_name: over.a_name ?? over.name,
      food_type_category: over.food_type_category,
    }) as MenuItemData;

  return [
    {
      food_type_id: 10001,
      type_name: 'Meal',
      a_type_name: 'Meal',
      type_limit: 0,
      selected_quantity: 0,
      type_data: [
        base_item({ id: 90001, name: 'Sample lunch', food_type_category: 'meal' }),
        base_item({ id: 90002, name: 'Sample dinner', food_type_category: 'meal' }),
      ],
    },
    {
      food_type_id: 10002,
      type_name: 'Snack',
      a_type_name: 'Snack',
      type_limit: 0,
      selected_quantity: 0,
      type_data: [base_item({ id: 90003, name: 'Sample snack', food_type_category: 'snack' })],
    },
  ];
}

function createGuestMenuPreviewStyles(c: ThemeColors) {
  return StyleSheet.create({
    scroll: { flex: 1 },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    planCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: 0,
      padding: DASH_STUDIO_INNER,
    },
    planLabel: { ...appTheme.typography.captionSmall, color: c.textSecondary, marginBottom: 4 },
    planTitle: { ...appTheme.typography.body, fontWeight: '700', color: c.text },
    hint: { ...appTheme.typography.caption, color: c.textSecondary, marginTop: appTheme.spacing.sm },
    loader: { paddingVertical: appTheme.spacing.lg, alignItems: 'center' },
    sectionTitle: {
      ...appTheme.typography.bodyMedium,
      fontWeight: '600',
      color: c.text,
      marginBottom: appTheme.spacing.sm,
    },
    section: {},
    sectionHeading: {
      ...appTheme.typography.caption,
      fontWeight: '700',
      color: c.textSecondary,
      marginBottom: appTheme.spacing.xs,
      textTransform: 'uppercase',
    },
  });
}

type GuestMenuPreviewRoute = RouteProp<RootStackParamList, 'GuestMenuPreview'>;

export function GuestMenuPreviewScreen() {
  const navigation = useAuthNavigation();
  const route = useRoute<GuestMenuPreviewRoute>();
  const { t } = useTranslation();
  const header_top = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const c = useThemeColors() ?? useThemeStore.getState().colors;
  const { openLogin, openRegister } = useAuthSheets();
  const is_authenticated = useAuthStore(s => s.isAuthenticated);
  const styles = useMemo(() => createGuestMenuPreviewStyles(c), [c]);
  const [plan, setPlan] = useState<Record<string, unknown> | null>(null);
  const route_plan = route.params?.plan;

  useEffect(() => {
    let cancelled = false;
    if (route_plan && typeof route_plan === 'object') {
      setPlan(route_plan);
      return () => {
        cancelled = true;
      };
    }
    getGuestExplorePlan().then(p => {
      if (cancelled) return;
      if (!p) {
        Alert.alert(t('common.error'), t('guest.missingPlan'), [{ text: t('common.ok'), onPress: () => navigation.goBack() }]);
        return;
      }
      setPlan(p);
    });
    return () => {
      cancelled = true;
    };
  }, [navigation, t, route_plan]);

  const preview_date = useMemo(() => moment().add(1, 'day').format('YYYY-MM-DD'), []);

  const { data: menu_builders_from_api, isPending: is_menu_loading } = useQuery({
    queryKey: ['guest-menu-preview', preview_date],
    queryFn: async () => {
      try {
        const res = await fetchGuestMenuPreview(preview_date);
        const builders = res.menu_builders;
        if (Array.isArray(builders) && builders.length > 0) {
          return builders as MenuBuilder[];
        }
      } catch {
        /* API or empty response — use mock below */
      }
      return buildMockGuestMenuBuilders();
    },
    enabled: plan != null,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: (failure_count, error) => resolveQueryRetry(failure_count, error, 1),
  });

  const menu_builders = menu_builders_from_api ?? null;

  const plan_title = useMemo(() => {
    if (!plan) return '';
    const p = plan as Record<string, unknown>;
    return (
      getApiText(p, 'title', 'arabic_title') ||
      (typeof p.title === 'string' ? p.title : '') ||
      (typeof p.name === 'string' ? p.name : '') ||
      t('subscriptions.chooseYourPlan')
    );
  }, [plan, t]);

  const meals_count = useMemo(() => {
    if (!plan) return 3;
    const p = plan as Record<string, unknown>;
    return Number(p.number_of_meals ?? p.total_meals ?? 3) || 3;
  }, [plan]);

  const snacks_count = useMemo(() => {
    if (!plan) return 2;
    const p = plan as Record<string, unknown>;
    return Number(p.number_of_snacks ?? p.total_snacks ?? 2) || 2;
  }, [plan]);

  const persist_and_leave_guest_preview = useCallback(async () => {
    if (!plan) {
      return;
    }
    try {
      if (!plan._guest_sample) {
        await setSelectedSubscription(plan);
      }
      await clearGuestExplorePlan();
    } catch {
      /* best-effort */
    }
  }, [plan]);

  const open_register_sheet = useCallback(() => {
    if (!plan) {
      return;
    }
    void (async () => {
      await persist_and_leave_guest_preview();
      openRegister(
        plan._guest_sample
          ? { presentation: 'default' }
          : { subData: plan, presentation: 'default' },
      );
    })();
  }, [plan, openRegister, persist_and_leave_guest_preview]);

  const open_login_sheet = useCallback(() => {
    if (!plan) {
      return;
    }
      void (async () => {
      await persist_and_leave_guest_preview();
      openLogin({ presentation: 'default' });
    })();
  }, [plan, openLogin, persist_and_leave_guest_preview]);

  /**
   * Guest cannot change quantities: offer Sign in or Create account via global auth sheets
   * (same modal pattern as the rest of the v2 app), on top of this screen.
   */
  const leave_guest_to_dashboard = useCallback(() => {
    const route_names = (navigation as { getState?: () => { routeNames?: string[] } })?.getState?.()?.routeNames;
    if (is_authenticated && Array.isArray(route_names) && route_names.includes('MainTabs')) {
      (navigation as { navigate: (n: string, p?: object) => void }).navigate('MainTabs', { screen: 'Dashboard' });
      return;
    }
    navigation.goBack();
  }, [is_authenticated, navigation]);

  const prompt_guest_auth = useCallback(() => {
    if (!plan) {
      return;
    }
    const title = t('auth.guestSignupTitle');
    const message = t('auth.guestSignupMessage');
    const signin_label = t('auth.signIn');
    const register_label = t('auth.createAccount');
    showActionSheet({
      title,
      message,
      cancelLabel: t('common.cancel'),
      options: [
        { label: signin_label, onSelect: open_login_sheet },
        { label: register_label, onSelect: open_register_sheet },
      ],
    });
  }, [plan, t, open_login_sheet, open_register_sheet]);

  if (!plan) {
    return (
      <StackScreenShell colors={c}>
        <ScreenHeader title={t('menu.menuSelection')} onBack={leave_guest_to_dashboard} scrollY={scrollY} />
        <View style={[styles.centered, { paddingTop: header_top }]}>
          <ActivityIndicator size="large" color={c.primary} />
        </View>
      </StackScreenShell>
    );
  }

  const builders = menu_builders ?? buildMockGuestMenuBuilders();
  const show_menu_spinner = is_menu_loading && menu_builders == null;

  return (
    <StackScreenShell colors={c}>
      <ScreenHeader title={t('menu.menuSelection')} onBack={leave_guest_to_dashboard} scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[styles.scrollContent, { paddingTop: header_top + 8, paddingBottom: 32 }]}
        showsVerticalScrollIndicator={false}>
        <View style={styles.planCard}>
          <Text style={styles.planLabel}>{t('guest.previewPlan')}</Text>
          <Text style={styles.planTitle} numberOfLines={2}>
            {plan_title}
          </Text>
          <Text style={styles.hint}>{t('guest.previewHint')}</Text>
        </View>

        <Text style={styles.sectionTitle}>
          {t('guest.sampleMenuForDate').replace('{{date}}', moment(preview_date).format('DD MMM YYYY'))}
        </Text>

        {show_menu_spinner ? (
          <View style={styles.loader}>
            <ActivityIndicator size="large" color={c.primary} />
          </View>
        ) : null}

        {builders.map((builder, builder_index) => (
          <View key={`guest-builder-${builder_index}-${String(builder.food_type_id ?? 'na')}`} style={styles.section}>
            <Text style={styles.sectionHeading}>{builder.type_name}</Text>
            {(builder.type_data ?? []).map((item: MenuItemData, item_index: number) => (
              <MenuFlowItemCard
                key={`guest-item-${builder_index}-${item_index}-${item.id ?? item.slug ?? item.selection_number ?? 'row'}`}
                themeStyleKey={`${c.primary}-${c.surface}`}
                item={item}
                menuBuilderId={Number(item.menu_builder_id ?? builder.food_type_id)}
                onQuantityChange={() => {}}
                mealsCount={meals_count}
                snacksCount={snacks_count}
                selectionDisabled
                onSelectionDisabledInteraction={prompt_guest_auth}
              />
            ))}
          </View>
        ))}
      </Animated.ScrollView>
    </StackScreenShell>
  );
}

export default GuestMenuPreviewScreen;
